import React from 'react'
import { observer } from 'mobx-react-lite'
import {
  MDBModal,
  MDBModalBody,
  MDBCard,
  MDBCardHeader,
  MDBCardBody,
  MDBRow,
  MDBCol,
  MDBContainer,
  MDBBtn,
  MDBBtnGroup,
  MDBIcon,
  MDBModalDialog,
  MDBModalContent,
} from 'mdb-react-ui-kit'
import MDB4Datatable from '../../../../components/content/MDB4Datatable/MDB4Datatable.component'
import { UIDatePickerInput } from './../../../../components/forms/form-fields'
import LoadingSpinner from './../../../../components/shared/LoadingSpinner.component'
import DateManager from '../../../../shared/utilities/DateManager.class'
import Papa from 'papaparse' // Import CSV library
import './ExportLeaderGrowthReportModal.scss'

const ExportLeaderGrowthReportModal = observer(
  ({
    isOpen,
    toggle,
    isLoading,
    fetching,
    exportmetric,
    setExportMetric,
    exportData,
    exportReportDate,
    onExportDateChange,
    onExportTimespanChange,
    exportTimespanInd,
    canAdvanceExportDate,
    buildTableRecords,
    renderPrevYearDropDown,
  }) => {
    const exportToCSV = () => {
      if (!exportData || !exportData.rows || exportData.rows.length === 0) {
        alert('No data to export!')
        return
      }

      const formattedData = exportData.rows.map(row => ({
        Agent: row.id || '',
        Usertype: row.usertype || '',
        'Report Timespan': row.timespan || '',
        'Current Value': row.current !== undefined ? row.current : 0, // Default to 0 if missing
        'Previous Value': row.previous !== undefined ? row.previous : 0, // Default to 0 if missing
        'Growth Value': row.growth !== undefined ? row.growth : '0%', // Default to '0%'
      }))

      // Convert formatted data to CSV format
      const csv = Papa.unparse(formattedData, { header: true })

      // Create a Blob and trigger CSV download
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'Team Growth Report.csv')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    }

    return (
      <MDBModal
        id='ExportLeaderGrowthReportModalComponent'
        open={isOpen}
        onClose={toggle}
        className='ExportLeaderGrowthReportModal'
      >
        <MDBModalDialog size='fullscreen'>
          <MDBModalContent>
            <MDBModalBody className={isLoading ? 'is-loading' : ''}>
              <MDBCard>
                <MDBCardHeader>
                  <div>Export Leader Growth Report</div>
                  <MDBBtn type='button' className='close-btn' color='warning' size='sm' block onClick={toggle}>
                    Close
                  </MDBBtn>
                </MDBCardHeader>
                <MDBCardBody>
                  {isLoading ? (
                    <div className='loading-wrapper'>
                      <LoadingSpinner size='md' isActive={true} />
                    </div>
                  ) : (
                    <>
                      <MDBRow>
                        <MDBContainer fluid>
                          <MDBCol size='12'>
                            <ul className='source-buttons'>
                              {[
                                'points',
                                'av',
                                'aca_lives',
                                'enrolled',
                                'ma_lives',
                                'life_av',
                                'med_supps_av',
                                'group',
                              ].map(metric => (
                                <li key={metric}>
                                  <MDBBtn
                                    type='button'
                                    disabled={fetching || exportmetric === metric}
                                    onClick={() => {
                                      setExportMetric(metric)
                                      buildTableRecords(metric)
                                    }}
                                    color={exportmetric !== metric ? 'indigo' : 'blue-grey'}
                                  >
                                    <MDBIcon icon='check' /> &nbsp;View {metric.replace(/_/g, ' ')}
                                  </MDBBtn>
                                </li>
                              ))}
                            </ul>
                          </MDBCol>
                        </MDBContainer>
                      </MDBRow>
                      <hr />
                      <MDBRow className='exporter-date-bar'>
                        <div>
                          <UIDatePickerInput
                            label='Report Date'
                            name='export_report_date'
                            id='export_dpw_report_date'
                            showYearDropdown
                            yearDropdownItemNumber={100}
                            scrollableYearDropdown
                            dateFormat='MM/dd/yyyy'
                            maxDate={new Date()}
                            selected={exportReportDate}
                            onChange={date => onExportDateChange(date?.target?.value)}
                          />
                        </div>
                        <div>
                          <MDBBtnGroup>
                            <MDBBtn
                              type='button'
                              disabled={fetching}
                              onClick={() => onExportDateChange(DateManager.getDayInPast('Sun', exportReportDate))}
                              color='indigo'
                            >
                              <MDBIcon icon='arrow-left' /> &nbsp;Previous Week
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              disabled={fetching}
                              onClick={() => onExportDateChange(new Date())}
                              color='indigo'
                            >
                              Current Week
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              disabled={fetching || !canAdvanceExportDate()}
                              onClick={() =>
                                onExportDateChange(
                                  DateManager.getDayInPast('Sun', DateManager.addWeeks(exportReportDate, 2))
                                )
                              }
                              color='indigo'
                            >
                              Next Week &nbsp; <MDBIcon icon='arrow-right' />
                            </MDBBtn>
                          </MDBBtnGroup>
                        </div>
                        <div>
                          <div className='prev-year-selector'>
                            <div>Previous Year</div>
                            <div className='ml-3'>
                              {fetching ? <i className='fa fa-spin fa-spinner'></i> : renderPrevYearDropDown()}
                            </div>
                          </div>
                        </div>
                        <div>
                          <MDBBtnGroup className='timespan-selector'>
                            <MDBBtn
                              type='button'
                              disabled={fetching || exportTimespanInd === 'WTD'}
                              onClick={() => onExportTimespanChange('WTD')}
                              color='indigo'
                            >
                              WTD
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              disabled={fetching || exportTimespanInd === 'MTD'}
                              onClick={() => onExportTimespanChange('MTD')}
                              color='indigo'
                            >
                              MTD
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              disabled={fetching || exportTimespanInd === 'QTD'}
                              onClick={() => onExportTimespanChange('QTD')}
                              color='indigo'
                            >
                              QTD
                            </MDBBtn>
                            <MDBBtn
                              type='button'
                              disabled={fetching || exportTimespanInd === 'YTD'}
                              onClick={() => onExportTimespanChange('YTD')}
                              color='indigo'
                            >
                              YTD
                            </MDBBtn>
                          </MDBBtnGroup>
                        </div>
                        <div>
                          <MDBBtn type='button' color='success' className='paddingSkip' size='md' onClick={exportToCSV}>
                            <MDBIcon icon='file-csv' /> Export CSV
                          </MDBBtn>
                        </div>
                      </MDBRow>
                      <hr />
                      <div>
                        <MDB4Datatable
                          data={exportData}
                          striped
                          small
                          order={['id', 'asc']}
                          bordered
                          entries={250}
                          entriesOptions={[250]}
                          displayEntries={false}
                        />
                      </div>
                    </>
                  )}
                </MDBCardBody>
              </MDBCard>
            </MDBModalBody>
          </MDBModalContent>
        </MDBModalDialog>
      </MDBModal>
    )
  }
)

export default ExportLeaderGrowthReportModal
